import { TagBadge } from "./TagBadge";
import { ScoreRing } from "./ScoreRing";
import { getTagGradient, getTopicImage } from "../utils/tagColors";
import { resolveMediaUrl } from "../api/client";
import type { ItemListEntry } from "../types";

function summaryToText(summary: string | null): string {
  if (!summary) return "";
  let s = summary.trim();
  // Set-Literal Format: {"- Text1","- Text2"} → aufdröseln
  if (s.startsWith("{") && s.endsWith("}") && s.includes('","')) {
    const inner = s.slice(1, -1);
    const parts = inner.match(/"([^"]*)"/g);
    if (parts) {
      s = parts.map((p) => p.replace(/^"|"$/g, "")).join("\n");
    } else {
      s = inner;
    }
  }
  if (s.startsWith("{") && s.endsWith("}")) {
    s = s.slice(1, -1).trim();
  }
  return s
    .split("\n")
    .map((l) => l.replace(/^[-*•"]\s*/, "").replace(/"\s*$/, "").trim())
    .filter((l) => l.length > 0)
    .join(" ");
}

function timeAgo(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffH = Math.floor((now.getTime() - d.getTime()) / 3600000);
  if (diffH < 1) return "Gerade eben";
  if (diffH < 24) return `Vor ${diffH} Std.`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return "Gestern";
  return `Vor ${diffD} Tagen`;
}

function cleanAuthor(author: string | null): string {
  if (!author) return "Unbekannt";
  const match = author.match(/"?([^"<]+)"?\s*<?/);
  return match?.[1]?.trim() || author;
}

interface HeroSectionProps {
  item: ItemListEntry;
  onSelect?: (id: number) => void;
  sourceLogo?: string | null;
}

export function HeroSection({ item, onSelect, sourceLogo }: HeroSectionProps) {
  const summaryText = summaryToText(item.summary);

  // Bild-Priorität: KI-Bild > Source-Logo > kuratiertes Stock-Foto
  const imageUrl = item.image_url
    ? resolveMediaUrl(item.image_url)
    : sourceLogo || getTopicImage(item.tags, item.id);

  return (
    <article
      className="hero-card"
      onClick={() => onSelect?.(item.id)}
    >
      {/* Hero Thumbnail (oben, volle Breite, 250px) */}
      <div
        className={`hero-card-thumb${sourceLogo && !item.image_url ? " has-logo" : ""}`}
      >
        <img
          src={imageUrl}
          alt=""
          loading="lazy"
          onError={(e) => {
            // Fallback: Topic-Bild versuchen, dann Gradient
            const fallback = getTopicImage(item.tags, item.id);
            if (e.currentTarget.src !== fallback) {
              e.currentTarget.src = fallback;
            } else {
              e.currentTarget.style.display = "none";
              e.currentTarget.parentElement!.style.background = getTagGradient(item.tags);
            }
          }}
        />
      </div>

      {/* Body (rahmenlos) */}
      <div className="hero-card-body-inner">
        {item.tags.length > 0 && (
          <div className="hero-card-tags">
            {item.tags.map((t) => (
              <TagBadge key={t} tag={t} />
            ))}
          </div>
        )}

        <h3 className="hero-card-title">{item.title || "Ohne Titel"}</h3>

        {summaryText && (
          <p className="hero-card-summary">{summaryText}</p>
        )}

        <div className="hero-card-footer">
          <span>
            {cleanAuthor(item.author)} · {item.published_at ? timeAgo(item.published_at) : ""}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ color: "var(--primary)", fontWeight: 500, fontSize: 13 }}>
              Mehr lesen →
            </span>
            {item.relevance_score !== null && (
              <ScoreRing score={item.relevance_score} size={38} />
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
